import React, { useEffect, useState } from 'react';
import {
  Box,
  MenuItem,
  Select,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { registerColors } from '../../styles/colors';
import RtlConfirmationDialog from '../../../../components/RtlConfirmationDialog/RtlConfirmationDialog';

type LanguageCode = 'en' | 'fr' | 'es' | 'ar';

const languages: { code: LanguageCode; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'ar', label: 'العربية' },
];

const getBrowserLang = (): LanguageCode => {
  const browserLang = navigator.language?.slice(0, 2) as LanguageCode;
  return languages.some((l) => l.code === browserLang) ? browserLang : 'en';
};

const LanguageSelector = () => {
  const { i18n } = useTranslation('common');
  const currentLang = i18n.language?.slice(0, 2) as LanguageCode;
  const value: LanguageCode = languages.some((l) => l.code === currentLang)
    ? currentLang
    : getBrowserLang();

  const [rtlDialogOpen, setRtlDialogOpen] = useState(false);

  useEffect(() => {
    if (!languages.some((l) => l.code === currentLang)) {
      i18n.changeLanguage(getBrowserLang());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLang, i18n]);

  const handleLanguageChange = (newLang: LanguageCode) => {
    if (newLang === 'ar' && i18n.language !== 'ar') {
      setRtlDialogOpen(true);
    } else {
      i18n.changeLanguage(newLang);
    }
  };

  const handleConfirmArabic = () => {
    setRtlDialogOpen(false);
    i18n.changeLanguage('ar');
  };

  const handleCancelArabic = () => {
    setRtlDialogOpen(false);
  };

  return (
    <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
      <Select
        value={value}
        onChange={(e) => handleLanguageChange(e.target.value as LanguageCode)}
        size="small"
        sx={{
          backgroundColor: registerColors.backgroundButton,
          color: 'white',
          border: `1px solid ${registerColors.borderSubtle}`,
          borderRadius: '8px',
          fontSize: '14px',
          height: '36px',
          minWidth: '120px',
          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.5)' },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              backgroundColor: registerColors.backgroundButton,
              border: `1px solid ${registerColors.borderSubtle}`,
              color: 'white',
              mt: 0.5,
              '& .MuiMenuItem-root': { fontSize: '14px' },
              '& .MuiMenuItem-root:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
              '& .MuiMenuItem-root.Mui-selected': { backgroundColor: 'rgba(255,255,255,0.12)' },
              '& .MuiMenuItem-root.Mui-selected:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
            },
          },
        }}
      >
        {languages.map((lang) => (
          <MenuItem key={lang.code} value={lang.code}>
            {lang.label}
          </MenuItem>
        ))}
      </Select>

      <RtlConfirmationDialog
        open={rtlDialogOpen}
        onConfirm={handleConfirmArabic}
        onCancel={handleCancelArabic}
      />
    </Box>
  );
};

export default LanguageSelector;
